import type { JobCardData } from '../../jobcard/components/JobCard/JobCard';

import { parseJobClickUrl } from '../../jobcard/util/parseJobClickUrl';
import { useDisplayLogContext } from '../context/DisplayLogContext';
import { useHomePageContext } from '../context/HomePageContext';

import {
    HostId,
    type JobcardDisplay,
    log as logJobCardDisplay,
    PageId,
    SectionId,
} from '@indeed/signals-schema/com/indeed/signals/schema/jobcards/JobcardDisplay';
import { useCallback, useRef } from 'react';

export type IFieldTypesKey = keyof typeof fieldTypes;

export type IFieldValue = boolean | string | string[] | number;

export interface DisplayLogParams {
    jobKey?: string;
    field: IFieldTypesKey;
    value: IFieldValue;
}
export const useLogDisplayToSignalsLogger = () => {
    const displayLogsRef = useDisplayLogContext();
    const logTrackerRef = useRef(new Map<string, boolean>());
    const homePageContext = useHomePageContext();

    return useCallback(
        (job: JobCardData, groupName: string) => {
            const jobKey = job.key;
            if (
                !displayLogsRef ||
                !displayLogsRef.current?.has(jobKey) ||
                // Only log once per job component mount
                logTrackerRef.current.has(jobKey)
            ) {
                return;
            }

            logTrackerRef.current.set(jobKey, true);
            const loggedFields = displayLogsRef.current.get(jobKey)!;
            const fieldsToLog: { [key: string]: { [key: string]: IFieldValue } } = {};
            for (const [fieldKey, fieldValue] of loggedFields) {
                // Guard against potentially logging a field that is not in the schema.
                // Types should prevent this, but being a bit more defensive
                // in case types are loosened in the future to not send
                // logs not meeting schema.
                if (fieldKey in fieldTypes) {
                    const fieldType = fieldTypes[fieldKey];
                    fieldsToLog[fieldKey] = {
                        [fieldType]: fieldValue,
                    };
                }
            }

            const { trackingKey } = parseJobClickUrl(job.tracking?.jobClick);
            const jobCardDisplayProps: JobcardDisplay = {
                jobKey: jobKey,
                hostId: { HostId: HostId.HOMEPAGE },
                pageId: { PageId: PageId.HOMEPAGE },
                sectionId: { SectionId: getSectionIdFromGroupName(groupName) },
                providerId: null,
                mobtk: trackingKey ? { string: trackingKey } : null,
                feedtk: trackingKey ? { string: trackingKey } : null,
                ctk: homePageContext.ctk ? { string: homePageContext.ctk } : null,
                ...fieldsToLog,
            };
            logJobCardDisplay(jobCardDisplayProps);

            // Clear out previous fields for job.
            displayLogsRef.current.delete(jobKey);
        },
        [displayLogsRef, homePageContext.ctk],
    );
};

const getSectionIdFromGroupName = (groupName: string): SectionId => {
    if (!groupName) {
        return SectionId.UNKNOWN;
    }
    switch (groupName.toLowerCase()) {
        case 'hp.jobsforyou':
        case 'hpd.jobsforyou':
            return SectionId.HP_JOBS_FOR_YOU;
        case 'hp.lastrq':
        case 'hpd.lastrq':
            return SectionId.HP_LAST_RQ;
        case 'hp.othermatches':
        case 'hpd.othermatches':
            return SectionId.HP_OTHER_MATCHES;
        case 'hp.autosourcermatches':
        case 'hpd.autosourcermatches':
            return SectionId.HP_AUTO_SOURCE_MATCHES;
        case 'hp.topapplicantfeed':
        case 'hpd.topapplicantfeed':
            return SectionId.HP_TOP_APPLICANT_FEED;
        case 'hp.relocationopportunities':
        case 'hpd.relocationopportunities':
            return SectionId.HP_RELOCATION_OPPORTUNITIES;
        default:
            return SectionId.UNKNOWN;
    }
};

export const useLogFeatureDisplay = () => {
    const displayLogsRef = useDisplayLogContext();

    return ({ jobKey, field, value }: DisplayLogParams) => {
        try {
            if (!jobKey || !displayLogsRef || !displayLogsRef.current) {
                return;
            }
            if (!displayLogsRef.current.has(jobKey)) {
                displayLogsRef.current.set(jobKey, new Map());
            }
            const log = displayLogsRef.current.get(jobKey)!;
            log.set(field, value);
        } catch (e) {
            // TODO look into logging jserr more gracefully.
        }
    };
};

const fieldTypes = {
    salaryYN: 'boolean',
    salary: 'string',
    sponsored: 'boolean',
    companyLogoYN: 'boolean',
    easyApplyBadgeYN: 'boolean',
    payNotProvidedYN: 'boolean',
    commuteEstimate: 'int',
    flair: 'string',
    is_urgently_hiring_eligible: 'boolean',
    newJobTagYN: 'boolean',
    jobType: 'string',
    jobTypeYN: 'boolean',
    jobTypeMatchResult: 'string',
    shift: 'string',
    shiftYN: 'boolean',
    shiftMatchResult: 'string',
    salaryMatchResult: 'string',
    benefits: 'array',
    brandedJobYN: 'boolean',
    brandedJobPackageYN: 'boolean',
    brandedJobPaidBrandingYN: 'boolean',
    brandedJobFeaturedEmployerYN: 'boolean',
    isFeaturedJobCardShown: 'boolean',
    enticersShown: 'array',
    isTopRatedEmployerYN: 'boolean',
    companyBannerYN: 'boolean',
    isInterviewNowEnticerShownYN: 'boolean',
};
